import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CampaignAssetRow {
  readonly campaignAssetId: string;
  readonly campaignAssetCode: string;
  readonly campaignRef: string;
  readonly campaignName: string | null;
  readonly assetId: string;
  readonly assetRole: string;
  readonly distributionCount: number;
  readonly status: string;
}

const ROLES = ['', 'CREATIVE', 'MEDIA_FILE', 'TEMPLATE', 'DELIVERABLE', 'REFERENCE'];
const CREATE_ROLES = ['CREATIVE', 'MEDIA_FILE', 'TEMPLATE', 'DELIVERABLE', 'REFERENCE'];
const STATUSES = ['', 'ACTIVE', 'ARCHIVED', 'REMOVED'];

/**
 * M36 — Campaign Asset Dashboard (§31). Register of assets linked to marketing
 * campaigns (T485) across the 5 campaign asset roles, showing per-link
 * distribution counts and the active/archived/removed status. Filterable by
 * campaign reference, asset role and status. Tenant isolation is enforced
 * server-side (LAW 4).
 */
export function CampaignAssetDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CampaignAssetRow[]>([]);
  const [total, setTotal] = useState(0);
  const [campaignRef, setCampaignRef] = useState('');
  const [assetRole, setAssetRole] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newCampaignRef, setNewCampaignRef] = useState('');
  const [newAssetId, setNewAssetId] = useState('');
  const [newRole, setNewRole] = useState(CREATE_ROLES[0]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (campaignRef) params.set('campaignRef', campaignRef);
    if (assetRole) params.set('assetRole', assetRole);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: CampaignAssetRow[]; total: number }>(`/v1/dam/campaigns/assets${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, campaignRef, assetRole, status, reload]);

  const on403 = (err: unknown): void => {
    if (err instanceof ApiError && err.status === 403) setNotice(t('m36.common.permRequired', { perm: 'M36_CAMPAIGN_MANAGE' }));
    else setNotice((err as Error).message);
  };

  const link = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setNotice(null);
    if (!newCampaignRef.trim()) { setNotice(t('m36.campaign.campaignRefReq')); return; }
    if (!newAssetId.trim()) { setNotice(t('m36.common.assetIdReq')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/dam/campaigns/assets`, {
        method: 'POST',
        body: { campaignRef: newCampaignRef.trim(), assetId: newAssetId.trim(), assetRole: newRole },
        token,
      });
      setNotice(t('m36.campaign.linked', { asset: newAssetId.trim(), campaign: newCampaignRef.trim() }));
      setNewAssetId('');
      setReload((n) => n + 1);
    } catch (err) {
      on403(err);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (r: CampaignAssetRow): Promise<void> => {
    setNotice(null);
    setBusyId(r.campaignAssetId);
    try {
      await apiRequest(`/v1/dam/campaigns/assets/${r.campaignAssetId}`, { method: 'DELETE', token });
      setNotice(t('m36.campaign.unlinked', { code: r.campaignAssetCode }));
      setReload((n) => n + 1);
    } catch (err) {
      on403(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="campaign-asset-dashboard">
      <h1>{t('m36.campaign.title')}</h1>
      <form aria-label="link-asset-form" onSubmit={(e) => void link(e)}>
        <label>
          {t('m36.campaign.campaignRef')}
          <input aria-label="field-campaign" value={newCampaignRef} onChange={(e) => setNewCampaignRef(e.target.value)} />
        </label>
        <label>
          {t('m36.common.asset')}
          <input aria-label="field-asset" value={newAssetId} onChange={(e) => setNewAssetId(e.target.value)} placeholder={t('m36.common.assetIdPh')} />
        </label>
        <label>
          {t('m36.common.role')}
          <select aria-label="field-role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
            {CREATE_ROLES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="link-asset" disabled={busy}>{t('m36.campaign.link')}</Button>
      </form>
      {notice && <p role="status" aria-label="campaign-notice">{notice}</p>}
      <div className="filters">
        <label>
          {t('m36.campaign.campaign')}
          <input aria-label="filter-campaign" value={campaignRef} onChange={(e) => setCampaignRef(e.target.value)} placeholder={t('m36.campaign.campaignRef')} />
        </label>
        <label>
          {t('m36.common.role')}
          <select aria-label="filter-role" value={assetRole} onChange={(e) => setAssetRole(e.target.value)}>
            {ROLES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="campaign-count">{t('m36.campaign.count', { count: total })}</p>
          <DataTable<CampaignAssetRow>
            ariaLabel="campaign-table"
            testId="campaign-table"
            rowKey={(r) => r.campaignAssetId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-role': r.assetRole })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.campaignAssetCode },
              { key: 'campaign', header: t('m36.campaign.campaign'), render: (r) => r.campaignName ?? r.campaignRef },
              { key: 'asset', header: t('m36.common.asset'), render: (r) => r.assetId },
              { key: 'role', header: t('m36.common.role'), render: (r) => r.assetRole },
              { key: 'distributions', header: t('m36.campaign.col.distributions'), render: (r) => r.distributionCount, cellAriaLabel: (r) => `dist-${r.campaignAssetId}` },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => (
                <>
                  {r.status !== 'REMOVED' && (
                    <Button variant="secondary" type="button" aria-label={`unlink-${r.campaignAssetCode}`} disabled={busyId === r.campaignAssetId} onClick={() => void unlink(r)}>{t('m36.campaign.unlink')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
